<template>
  <div class="step2-wrapper">
    <div class="step-details">
      <div class="step2-left">
        <details-field-group
          :id="uuid"
          label="Library Details"
          :is-bio-styling="true"
          :collapsed="false"
        >
          <BioMaterialView
            :material-type="materialType"
            :uuid="uuid"
            :hide-title="true"
            :group-nid="groupNid"
            :omit-fields="editFields"
            :show-add-to-library="false"
            :bio-material-data="null"
            :show-library-style="true"
          />
        </details-field-group>
      </div>
      <div class="step2-right">
        <radio-set
          name="BioMaterialStepChooser"
          :options="getOptions"
          label-name=""
          @radio-selection="selected($event)"
          id="bio-material-step-chooser"
        />
      </div>
    </div>
    <div class="buttontray">
      <v-button :class="disableButton" type="submit" @click="clickContinue">
        Continue
      </v-button>
    </div>
  </div>
</template>

<script>
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import RadioSet from "@/components/fields/RadioSet.vue";
import DetailsFieldGroup from "../raft/DetailsFieldGroup.vue";
import BioMaterialView from "./BioMaterialView.vue";
import { restClient } from "../../lib/raftApi";
import VButton from "../raft/VButton.vue";

const { config } = useDrupalSettings();

export default {
  name: "BioMaterialStepChooser",
  components: {
    BioMaterialView,
    RadioSet,
    DetailsFieldGroup,
    VButton,
  },
  props: {
    materialType: { type: String, required: true },
    groupNid: { type: String, required: true },
    uuid: { type: String, required: true },
  },
  data() {
    return {
      fields: {},
      editFields: [],
      submitDisabled: true,
      selectedAction: "",
    };
  },
  methods: {
    selected(value) {
      this.selectedAction = value;
      this.$emit("choose-modification", value === "modify");
      this.submitDisabled = false;
    },
    clickContinue() {
      this.$emit("bio-material-step-chooser-selection", this.selectedAction);
    },
  },
  computed: {
    levelsTranslation() {
      return config.bioRegulations === "swiss"
        ? "containment level"
        : "biosafety level";
    },
    getOptions() {
      return [
        {
          value: "library",
          text:
            "Use this material exactly as described in the " +
            this.customerShortName +
            " library",
        },
        {
          value: "modify",
          text: `I want to make changes (such as genetic modifications, strain, or ${this.levelsTranslation})`,
        },
      ];
    },
    bundleFieldsSorted() {
      const bundleFieldsSorted = [];
      if (this.fields) {
        Object.values(this.fields).forEach((fieldData) => {
          if (fieldData?.form_displays?.default) {
            bundleFieldsSorted.push(fieldData);
          }
        });
        bundleFieldsSorted.sort(
          (a, b) =>
            a.form_displays.default.weight - b.form_displays.default.weight,
        );
      }
      return bundleFieldsSorted;
    },
    disableButton() {
      return this.submitDisabled ? "disabled" : "primary";
    },
    customerShortName() {
      return config.customerShortName || "";
    },
  },
  async mounted() {
    this.$emit(
      "change-title",
      `Add Library Item to ${config.groupTitle || "Group"} or Edit`,
    );
    const restPath = "/fields/bio_material/" + this.materialType;
    this.fields = await restClient
      .get({
        path: restPath,
        queries: {
          wrap_allowed_values: 1,
        },
      })
      .catch(({ message, data }) => {
        this.errorEmit(message, data);
        this.$showNotification(
          "error",
          "Unable to obtain material. Please try again.",
        );
      });
    Object.values(this.bundleFieldsSorted).forEach((fieldData) => {
      this.editFields.push(fieldData.field_name);
    });
  },
};
</script>

<style scoped>
.step2-wrapper {
  min-height: calc(0.6rem + 11.05rem * 2.5);
  display: grid;
  grid-template-rows: 1fr min-content;
}
.step2-wrapper .step-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
}

.step2-left {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}

.step2-left h2 {
  margin-bottom: 0.5rem;
}

.step2-right {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  font-size: var(--font-size-100);
  line-height: 1.4rem;
}

.buttontray button {
  margin: 0.5rem 0.5rem 0.5rem auto;
}
</style>

<style scoped>
#bio-material-step-chooser :deep(label) {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  cursor: pointer;
  border: 1px solid transparent;
  padding: 0.75rem;
  border-radius: 0.5em;
}
#bio-material-step-chooser :deep(label > input) {
  width: auto;
}

#bio-material-step-chooser :deep(label:has(> input:checked)) {
  color: var(--blue-700);
  border: 1px solid var(--blue-700);
}
</style>
